// Spell sounds: one short WebAudio synth per spell (spells that fit share
// one, e.g. Fireball and Delayed Fireball). They plug into the Sound system
// in audio.ts, so the volume settings apply and a recorded file at
// /sounds/<name>.mp3 (e.g. sounds/fireball.mp3) replaces the synth.
//
// This module must not import audio.ts at runtime (audio.ts imports it).
import { BLIZZARD_STARTUP_SECONDS, DISINTEGRATE_DELAY_SECONDS, FLASH_CHARGE_SECONDS, METEOR_STARTUP_SECONDS } from "@wp/shared";

export const SPELL_SOUNDS = [
  "fireball",
  "flames",
  "ice",
  "missile",
  "meteor",
  "bugs",
  "spider",
  "rainbow",
  "shield",
  "mirror",
  "stone",
  "bats",
  "fart",
  "warble",
  "portal",
  "alarm",
  "reveal",
  "lock",
  "drain",
  "dark",
  "cleanse",
  "dizzy",
  "zap",
  "life",
  "surge",
  "flashbang",
  "swap",
  "blizzard",
  "blaster",
  "rocks",
  "splat",
  "squelch",
  "monkey",
  "storm",
  "chainZap",
  "bonkCast",
  "boomCast",
  "treasure",
  "charm",
  // Not cast sounds: a Control Weather lightning strike, a Bonk trap going
  // off on someone's head, a Boom trap exploding (a recorded file:
  // public/sounds/boomTrap.mp3).
  "thunder",
  "rumble",
  "bonk",
  "boomTrap",
  // Not cast sounds: Flash going off (the bang, and the ringing in your ears after it).
  "bang",
  "tinnitus",
] as const;

export type SpellSound = (typeof SPELL_SOUNDS)[number];

/** Which sound a spell plays when it is assembled (null = the generic "spell" sparkle). */
const BY_SPELL: Record<string, SpellSound> = {
  fireball: "fireball",
  delayed_fireball: "fireball",
  ignite: "flames",
  chill_ray: "ice",
  magic_missile: "missile",
  meteor_swarm: "meteor",
  insect: "bugs",
  arachnid: "spider",
  color_spray: "rainbow",
  barrier: "shield",
  counterspell: "mirror",
  stoneskin: "stone",
  vampire: "bats",
  fart: "fart",
  card_illusion: "warble",
  // Fake Out must sound exactly like Card Illusion.
  fake_out: "warble",
  hallucination: "warble",
  banishment: "portal",
  alarm: "alarm",
  // False Alarm sounds exactly like Flash (a harmless flashbang).
  alarm_illusion: "flashbang",
  arcane_eye: "reveal",
  arcane_lock: "lock",
  blight: "drain",
  blindness: "dark",
  cleanse: "cleanse",
  confusion: "dizzy",
  disintegrate: "zap",
  false_life: "life",
  overcharge: "surge",
  flash: "flashbang",
  switcheroo: "swap",
  blizzard: "blizzard",
  blaster: "blaster",
  rock_blast: "rocks",
  dung_fling: "splat",
  grease: "squelch",
  control_weather: "storm",
  chain_lightning: "chainZap",
  bonk: "bonkCast",
  boom: "boomCast",
  charm: "charm",
};

export function spellSound(spellId: string): SpellSound | null {
  return BY_SPELL[spellId] ?? null;
}

/** Sounds played on top of a spell's own cast sound (everyone at the table hears them). */
const EXTRA_BY_SPELL: Record<string, readonly SpellSound[]> = {
  dung_fling: ["monkey"],
};

export function spellExtraSounds(spellId: string): readonly SpellSound[] {
  return EXTRA_BY_SPELL[spellId] ?? [];
}

/** Base loudness of each spell sound before the volume settings (0..1). */
export const SPELL_SOUND_LEVEL: Record<SpellSound, number> = {
  fireball: 0.45,
  flames: 0.4,
  ice: 0.3,
  missile: 0.28,
  meteor: 0.5,
  bugs: 0.25,
  spider: 0.3,
  rainbow: 0.22,
  shield: 0.3,
  mirror: 0.25,
  stone: 0.45,
  bats: 0.22,
  fart: 0.4,
  warble: 0.25,
  portal: 0.3,
  alarm: 0.22,
  reveal: 0.25,
  lock: 0.4,
  drain: 0.3,
  dark: 0.35,
  cleanse: 0.25,
  dizzy: 0.25,
  zap: 0.3,
  life: 0.25,
  surge: 0.3,
  flashbang: 0.4,
  swap: 0.25,
  blizzard: 0.45,
  blaster: 0.55,
  rocks: 0.45,
  splat: 0.45,
  squelch: 0.4,
  monkey: 0.4,
  storm: 0.35,
  chainZap: 0.35,
  bonkCast: 0.3,
  boomCast: 0.3,
  treasure: 0.3,
  charm: 0.3,
  thunder: 0.55,
  rumble: 0.14,
  bonk: 0.5,
  boomTrap: 0.7,
  bang: 0.4,
  tinnitus: 0.16,
};

// ---------- building blocks ----------

type Synth = (ctx: AudioContext, out: AudioNode, t0: number) => void;

/** An enveloped oscillator; returns it so callers can bend the pitch. */
function osc(ctx: AudioContext, out: AudioNode, t0: number, freq: number, ms: number, type: OscillatorType, level = 1, attack = 0.01): OscillatorNode {
  const t1 = t0 + ms / 1000;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(level, t0 + Math.min(attack, ms / 2000));
  g.gain.exponentialRampToValueAtTime(0.0001, t1);
  o.connect(g).connect(out);
  o.start(t0);
  o.stop(t1 + 0.02);
  return o;
}

/** Pitch slide from `f0` to `f1`. */
function slide(ctx: AudioContext, out: AudioNode, t0: number, f0: number, f1: number, ms: number, type: OscillatorType, level = 1, attack = 0.01): OscillatorNode {
  const o = osc(ctx, out, t0, f0, ms, type, level, attack);
  o.frequency.exponentialRampToValueAtTime(f1, t0 + ms / 1000);
  return o;
}

/** A tone swelling from silence to `level` over its whole length (pitch sliding f0 -> f1), cut off at the end. */
function swell(ctx: AudioContext, out: AudioNode, t0: number, f0: number, f1: number, ms: number, type: OscillatorType, level: number): OscillatorNode {
  const t1 = t0 + ms / 1000;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(f1, t1);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(level, t1 - 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, t1 + 0.06);
  o.connect(g).connect(out);
  o.start(t0);
  o.stop(t1 + 0.1);
  return o;
}

/** Add vibrato (an LFO on the pitch). */
function vibrato(ctx: AudioContext, o: OscillatorNode, t0: number, ms: number, rate: number, depth: number): void {
  const lfo = ctx.createOscillator();
  const g = ctx.createGain();
  lfo.frequency.value = rate;
  g.gain.value = depth;
  lfo.connect(g).connect(o.frequency);
  lfo.start(t0);
  lfo.stop(t0 + ms / 1000 + 0.05);
}

const noiseBuffers = new WeakMap<AudioContext, AudioBuffer>();

/** Filtered white noise with an envelope (one shared 2 s buffer per context). */
function hiss(ctx: AudioContext, out: AudioNode, t0: number, ms: number, filter: BiquadFilterType, freq: number, q: number, level: number, attack = 0.005): BiquadFilterNode {
  let buffer = noiseBuffers.get(ctx);
  if (!buffer) {
    buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = buffer.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    noiseBuffers.set(ctx, buffer);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const f = ctx.createBiquadFilter();
  f.type = filter;
  f.frequency.setValueAtTime(freq, t0);
  f.Q.value = q;
  const g = ctx.createGain();
  const t1 = t0 + ms / 1000;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(level, t0 + Math.min(attack, ms / 2000));
  g.gain.exponentialRampToValueAtTime(0.0001, t1);
  src.connect(f).connect(g).connect(out);
  src.start(t0, Math.random());
  src.stop(t1 + 0.02);
  return f;
}

/** A low falling thump (impacts). */
function thump(ctx: AudioContext, out: AudioNode, t0: number, f0 = 130, level = 1): void {
  slide(ctx, out, t0, f0, 35, 320, "sine", level, 0.006);
}

/** An echo send that cleans itself up. */
function echo(ctx: AudioContext, out: AudioNode, time: number, feedback: number, ms = 2500): AudioNode {
  const d = ctx.createDelay();
  const fb = ctx.createGain();
  d.delayTime.value = time;
  fb.gain.value = feedback;
  d.connect(fb).connect(d);
  d.connect(out);
  setTimeout(() => {
    d.disconnect();
    fb.disconnect();
  }, ms);
  return d;
}

/** A low rolling rumble (thunder, explosions dying away). */
function rumbleAt(ctx: AudioContext, out: AudioNode, t: number, ms: number, level: number): void {
  const f = hiss(ctx, out, t, ms, "lowpass", 260, 0.9, level, 0.06);
  f.frequency.exponentialRampToValueAtTime(90, t + ms / 1000);
  const o = slide(ctx, out, t, 55, 32, ms, "sine", level * 0.6, 0.08);
  vibrato(ctx, o, t, ms, 7, 6);
}

/** A hollow wooden knock. */
function woodAt(ctx: AudioContext, out: AudioNode, t: number, level: number): void {
  slide(ctx, out, t, 720, 380, 90, "triangle", level, 0.002);
  hiss(ctx, out, t, 50, "bandpass", 1800, 3, level * 0.6, 0.001);
}

/** The flashbang going off. */
function bangAt(ctx: AudioContext, out: AudioNode, t: number): void {
  hiss(ctx, out, t, 900, "lowpass", 2500, 0.7, 1);
  thump(ctx, out, t, 140, 1);
  hiss(ctx, out, t, 1300, "highpass", 6000, 0.5, 0.3);
}

// ---------- the sounds ----------

export const SPELL_SYNTHS: Record<SpellSound, Synth> = {
  // A roaring whoosh that swells, then a fiery boom.
  fireball: (ctx, out, t0) => {
    const w = hiss(ctx, out, t0, 650, "bandpass", 400, 1.2, 0.7, 0.4);
    w.frequency.exponentialRampToValueAtTime(1400, t0 + 0.6);
    hiss(ctx, out, t0 + 0.6, 700, "lowpass", 900, 0.8, 1);
    thump(ctx, out, t0 + 0.6, 110, 1);
    hiss(ctx, out, t0 + 0.65, 500, "highpass", 3000, 0.6, 0.25);
  },

  // Crackling flames: a low roar with random pops.
  flames: (ctx, out, t0) => {
    hiss(ctx, out, t0, 1100, "lowpass", 600, 0.7, 0.6, 0.15);
    for (let i = 0; i < 12; i++) {
      hiss(ctx, out, t0 + Math.random() * 0.95, 25, "highpass", 2500 + Math.random() * 3000, 1, 0.5);
    }
  },

  // A glassy descending shimmer over a cold hiss, then a crystal tinkle.
  ice: (ctx, out, t0) => {
    hiss(ctx, out, t0, 700, "highpass", 5000, 0.7, 0.35, 0.05);
    [2637, 2349, 1976, 1760, 1568].forEach((f, i) => osc(ctx, out, t0 + i * 0.06, f, 350, "sine", 0.4));
    for (let i = 0; i < 5; i++) osc(ctx, out, t0 + 0.45 + i * 0.05, 3500 + Math.random() * 2500, 120, "triangle", 0.25);
  },

  // Three quick zips, each ending in a small pop.
  missile: (ctx, out, t0) => {
    for (let i = 0; i < 3; i++) {
      const t = t0 + i * 0.16;
      slide(ctx, out, t, 1800, 500, 180, "square", 0.25);
      hiss(ctx, out, t + 0.17, 60, "bandpass", 1200, 1, 0.6);
    }
  },

  // A rumbling sky that builds while the meteors fall (METEOR_STARTUP_SECONDS),
  // their whistles closing in, then a run of heavy impacts as they land.
  meteor: (ctx, out, t0) => {
    const hit = t0 + METEOR_STARTUP_SECONDS;
    const r = hiss(ctx, out, t0, METEOR_STARTUP_SECONDS * 1000 + 600, "lowpass", 120, 0.7, 0.75, METEOR_STARTUP_SECONDS - 0.3);
    r.frequency.exponentialRampToValueAtTime(320, hit);
    osc(ctx, out, t0 + 0.4, 48, (METEOR_STARTUP_SECONDS - 0.2) * 1000, "sine", 0.35, METEOR_STARTUP_SECONDS - 0.6);
    // The roar swells just before they land.
    hiss(ctx, out, hit - 1.4, 1900, "lowpass", 260, 0.7, 0.6, 0.95);
    for (let i = 0; i < 6; i++) {
      const t = hit + i * 0.13 + Math.random() * 0.05;
      // Each one whistles down for the last stretch before it lands.
      slide(ctx, out, t - 1.1, 1400 + Math.random() * 300, 220, 1100, "sine", 0.06, 0.8);
      thump(ctx, out, t, 90 + Math.random() * 60, 0.9);
      hiss(ctx, out, t, 300, "lowpass", 1200, 0.7, 0.55);
    }
    hiss(ctx, out, hit + 0.75, 900, "lowpass", 500, 0.7, 0.35);
  },

  // Buzzing wings and little clicking legs.
  bugs: (ctx, out, t0) => {
    const b = osc(ctx, out, t0, 210, 900, "sawtooth", 0.25, 0.08);
    vibrato(ctx, b, t0, 900, 28, 40);
    for (let i = 0; i < 10; i++) hiss(ctx, out, t0 + 0.1 + i * 0.08 + Math.random() * 0.03, 15, "bandpass", 4000, 3, 0.6);
  },

  // A silk thread zipping down, a soft boing, then skittering legs.
  spider: (ctx, out, t0) => {
    slide(ctx, out, t0, 1400, 300, 500, "triangle", 0.4);
    const b = slide(ctx, out, t0 + 0.5, 180, 120, 350, "sine", 0.6);
    vibrato(ctx, b, t0 + 0.5, 350, 12, 25);
    for (let i = 0; i < 8; i++) hiss(ctx, out, t0 + 0.9 + i * 0.05, 12, "bandpass", 3000, 4, 0.5);
  },

  // A bright run up a major scale, every note a different colour.
  rainbow: (ctx, out, t0) => {
    const e = echo(ctx, out, 0.09, 0.3);
    [523, 587, 659, 784, 880, 1047, 1175, 1319].forEach((f, i) => {
      osc(ctx, out, t0 + i * 0.045, f, 220, "triangle", 0.5);
      osc(ctx, e, t0 + i * 0.045, f * 2, 120, "sine", 0.2);
    });
  },

  // A resonant hum with a bell-like strike: a shield going up.
  shield: (ctx, out, t0) => {
    const h = slide(ctx, out, t0, 110, 220, 900, "triangle", 0.4, 0.2);
    vibrato(ctx, h, t0, 900, 6, 4);
    osc(ctx, out, t0 + 0.15, 880, 900, "sine", 0.35);
    osc(ctx, out, t0 + 0.15, 1320, 700, "sine", 0.2);
  },

  // A mirror ping that rings forward and back.
  mirror: (ctx, out, t0) => {
    const e = echo(ctx, out, 0.13, 0.35);
    slide(ctx, e, t0, 900, 1800, 160, "sine", 0.5);
    slide(ctx, out, t0 + 0.18, 1800, 900, 160, "sine", 0.5);
    osc(ctx, out, t0 + 0.34, 2400, 400, "triangle", 0.3);
  },

  // Grinding rock settling into a shell.
  stone: (ctx, out, t0) => {
    for (let i = 0; i < 5; i++) {
      const t = t0 + i * 0.12;
      hiss(ctx, out, t, 140, "lowpass", 500 + Math.random() * 300, 1, 0.7);
      thump(ctx, out, t, 70 + Math.random() * 30, 0.5);
    }
    hiss(ctx, out, t0 + 0.6, 400, "bandpass", 250, 0.8, 0.5);
  },

  // High bat squeaks and fluttering wings.
  bats: (ctx, out, t0) => {
    for (let i = 0; i < 5; i++) {
      const t = t0 + i * 0.17 + Math.random() * 0.05;
      slide(ctx, out, t, 3200 + Math.random() * 800, 2200, 70, "sine", 0.35);
    }
    for (let i = 0; i < 14; i++) hiss(ctx, out, t0 + i * 0.06, 30, "bandpass", 700, 1.5, 0.35);
  },

  // A rude, wobbling raspberry.
  fart: (ctx, out, t0) => {
    const r = slide(ctx, out, t0, 95, 60, 750, "sawtooth", 0.6, 0.03);
    vibrato(ctx, r, t0, 750, 23, 18);
    hiss(ctx, out, t0, 700, "lowpass", 400, 0.8, 0.4, 0.05);
  },

  // A woozy, wobbling chord (illusions and distortions).
  warble: (ctx, out, t0) => {
    for (const [f, d] of [
      [440, 18],
      [554, 22],
      [659, 26],
    ] as const) {
      const o = osc(ctx, out, t0, f, 1000, "sine", 0.25, 0.15);
      vibrato(ctx, o, t0, 1000, 5 + Math.random() * 3, d);
    }
  },

  // A swirling whoosh that spirals down into the portal.
  portal: (ctx, out, t0) => {
    const o = slide(ctx, out, t0, 700, 90, 1500, "triangle", 0.4, 0.1);
    vibrato(ctx, o, t0, 1500, 9, 60);
    const f = hiss(ctx, out, t0, 1500, "bandpass", 1500, 4, 0.5, 0.3);
    f.frequency.exponentialRampToValueAtTime(200, t0 + 1.5);
  },

  // A two-tone bell ringing.
  alarm: (ctx, out, t0) => {
    for (let i = 0; i < 6; i++) {
      const f = i % 2 ? 990 : 1320;
      osc(ctx, out, t0 + i * 0.11, f, 160, "square", 0.25);
      osc(ctx, out, t0 + i * 0.11, f * 2.7, 120, "sine", 0.15);
    }
  },

  // A mystic rising tone with vibrato (seeing something hidden).
  reveal: (ctx, out, t0) => {
    const o = slide(ctx, out, t0, 330, 990, 700, "sine", 0.45, 0.1);
    vibrato(ctx, o, t0, 700, 7, 12);
    osc(ctx, out, t0 + 0.6, 1320, 500, "triangle", 0.3);
    osc(ctx, out, t0 + 0.6, 1980, 400, "sine", 0.15);
  },

  // A clank and a click-click of a padlock shutting.
  lock: (ctx, out, t0) => {
    hiss(ctx, out, t0, 60, "bandpass", 2000, 2, 0.8);
    osc(ctx, out, t0, 1400, 80, "square", 0.25);
    hiss(ctx, out, t0 + 0.25, 40, "bandpass", 3500, 3, 0.8);
    hiss(ctx, out, t0 + 0.33, 40, "bandpass", 3500, 3, 0.8);
    thump(ctx, out, t0 + 0.25, 180, 0.5);
  },

  // A sickly downward suck.
  drain: (ctx, out, t0) => {
    const o = slide(ctx, out, t0, 520, 110, 1000, "sawtooth", 0.18, 0.05);
    vibrato(ctx, o, t0, 1000, 7, 20);
    const f = hiss(ctx, out, t0, 1000, "bandpass", 1600, 3, 0.4, 0.1);
    f.frequency.exponentialRampToValueAtTime(250, t0 + 1);
  },

  // Darkness falls: a low swelling drone and a deep thud.
  dark: (ctx, out, t0) => {
    slide(ctx, out, t0, 160, 55, 1100, "sawtooth", 0.25, 0.3);
    slide(ctx, out, t0, 80, 40, 1100, "sine", 0.6, 0.3);
    thump(ctx, out, t0 + 0.7, 90, 0.7);
  },

  // Bubbly rising notes (washing a card clean).
  cleanse: (ctx, out, t0) => {
    for (let i = 0; i < 9; i++) {
      const f = 500 + Math.random() * 900;
      slide(ctx, out, t0 + i * 0.07, f, f * 1.8, 90, "sine", 0.35);
    }
    osc(ctx, out, t0 + 0.65, 1568, 500, "triangle", 0.25);
  },

  // A wobbling slide whistle going round and round.
  dizzy: (ctx, out, t0) => {
    const o = osc(ctx, out, t0, 600, 1100, "sine", 0.4, 0.05);
    vibrato(ctx, o, t0, 1100, 3.5, 250);
    hiss(ctx, out, t0 + 0.9, 80, "bandpass", 900, 2, 0.4);
  },

  // Disintegrate: the orbital laser charging up in the sky (a low hum and a
  // rising whine for DISINTEGRATE_DELAY_SECONDS), then the beam strikes: a
  // searing zap, a thump and the sizzle of the scorch.
  zap: (ctx, out, t0) => {
    const go = DISINTEGRATE_DELAY_SECONDS;
    const ms = go * 1000;
    const hum = swell(ctx, out, t0, 55, 165, ms, "sawtooth", 0.22);
    vibrato(ctx, hum, t0, ms, 6, 4);
    const body = swell(ctx, out, t0, 110, 330, ms, "sine", 0.35);
    vibrato(ctx, body, t0, ms, 9, 6);
    const whine = swell(ctx, out, t0 + go * 0.4, 500, 2400, ms * 0.6, "sine", 0.12);
    vibrato(ctx, whine, t0 + go * 0.4, ms * 0.6, 14, 20);
    const t = t0 + go;
    slide(ctx, out, t, 3200, 140, 520, "sawtooth", 0.32, 0.004);
    slide(ctx, out, t, 2600, 110, 520, "square", 0.12, 0.004);
    hiss(ctx, out, t, 260, "highpass", 2500, 0.8, 0.5, 0.004);
    thump(ctx, out, t + 0.02, 120, 0.8);
    hiss(ctx, out, t + 0.15, 900, "bandpass", 4200, 1.2, 0.18, 0.05);
    for (let i = 0; i < 10; i++) hiss(ctx, out, t + 0.25 + i * 0.07 + Math.random() * 0.04, 30, "highpass", 1500 + Math.random() * 3000, 1, 0.35);
  },

  // A warm rising chord (borrowed life).
  life: (ctx, out, t0) => {
    [262, 330, 392, 523].forEach((f, i) => slide(ctx, out, t0 + i * 0.09, f, f * 1.5, 700, "triangle", 0.3, 0.1));
    osc(ctx, out, t0 + 0.5, 1047, 500, "sine", 0.2);
  },

  // An electric surge rising in pitch with crackles.
  surge: (ctx, out, t0) => {
    const o = slide(ctx, out, t0, 80, 600, 800, "sawtooth", 0.3, 0.05);
    vibrato(ctx, o, t0, 800, 50, 30);
    for (let i = 0; i < 8; i++) hiss(ctx, out, t0 + Math.random() * 0.8, 25, "highpass", 4000, 1, 0.45);
  },

  // A stun grenade: metal clinks as it hits the table and bounces, the spoon
  // pinging off, a fizz, then the bang (timed to the FLASH_CHARGE_SECONDS charge).
  flashbang: (ctx, out, t0) => {
    const go = t0 + FLASH_CHARGE_SECONDS - 0.05;
    for (const [dt, lv] of [
      [0.75, 1],
      [1.2, 0.6],
      [1.44, 0.35],
    ] as const) {
      osc(ctx, out, t0 + dt, 2100 + Math.random() * 300, 170, "triangle", 0.35 * lv);
      osc(ctx, out, t0 + dt, 3500 + Math.random() * 300, 100, "sine", 0.22 * lv);
      hiss(ctx, out, t0 + dt, 40, "bandpass", 2600, 2, 0.6 * lv);
      thump(ctx, out, t0 + dt, 170, 0.45 * lv);
    }
    slide(ctx, out, t0 + 0.78, 4200, 2700, 240, "sine", 0.18);
    const fizzMs = (go - t0 - 1.5) * 1000;
    hiss(ctx, out, t0 + 1.5, fizzMs, "highpass", 3200, 0.7, 0.35, fizzMs / 1000 - 0.35);
    for (let i = 0; i < 14; i++) hiss(ctx, out, t0 + 1.55 + Math.random() * (fizzMs / 1000 - 0.1), 20, "highpass", 5000, 1, 0.3);
    bangAt(ctx, out, go);
  },

  // Flash going off (for whoever did not hear the grenade land).
  bang: (ctx, out, t0) => bangAt(ctx, out, t0),

  // The ringing in your ears after a flashbang: a thin, high, slightly beating
  // sine that stings at first and fades away over a few seconds.
  tinnitus: (ctx, out, t0) => {
    const f = 3500 + Math.random() * 300;
    const a = osc(ctx, out, t0 + 0.05, f, 3000, "sine", 1, 0.04);
    vibrato(ctx, a, t0 + 0.05, 3000, 5, 4);
    osc(ctx, out, t0 + 0.05, f * 1.006, 2400, "sine", 0.35, 0.04);
    osc(ctx, out, t0 + 0.05, f * 2.01, 900, "sine", 0.12, 0.02);
  },

  // Charm: a dreamy harp glissando up, a soft two-note "aww" swoon and a sparkle on top.
  charm: (ctx, out, t0) => {
    const e = echo(ctx, out, 0.12, 0.35);
    [523, 659, 784, 988, 1175, 1319, 1568].forEach((f, i) => {
      osc(ctx, out, t0 + i * 0.05, f, 380, "triangle", 0.35);
      osc(ctx, e, t0 + i * 0.05, f * 2, 220, "sine", 0.12);
    });
    const a = slide(ctx, out, t0 + 0.45, 880, 660, 420, "sine", 0.35, 0.08);
    vibrato(ctx, a, t0 + 0.45, 420, 6, 10);
    const b = slide(ctx, out, t0 + 0.85, 784, 523, 520, "sine", 0.3, 0.08);
    vibrato(ctx, b, t0 + 0.85, 520, 6, 10);
    for (let i = 0; i < 5; i++) osc(ctx, e, t0 + 0.5 + Math.random() * 0.8, 2600 + Math.random() * 1600, 90, "sine", 0.15);
  },

  // Two slide whistles crossing (one up, one down).
  swap: (ctx, out, t0) => {
    slide(ctx, out, t0, 400, 1200, 450, "sine", 0.4);
    slide(ctx, out, t0, 1200, 400, 450, "triangle", 0.3);
    osc(ctx, out, t0 + 0.47, 880, 160, "sine", 0.3);
    osc(ctx, out, t0 + 0.55, 1320, 200, "sine", 0.3);
  },

  // Wind rising for the BLIZZARD_STARTUP_SECONDS wind-up, then an icy crash.
  blizzard: (ctx, out, t0) => {
    const go = BLIZZARD_STARTUP_SECONDS;
    const w = hiss(ctx, out, t0, go * 1000 + 200, "bandpass", 280, 1.4, 0.75, go - 0.4);
    w.frequency.exponentialRampToValueAtTime(1500, t0 + go);
    hiss(ctx, out, t0, go * 1000 + 100, "lowpass", 350, 0.7, 0.5, go - 0.8);
    const o = slide(ctx, out, t0 + 0.5, 500, 1100, (go - 0.5) * 1000, "sine", 0.05, go - 1);
    vibrato(ctx, o, t0 + 0.5, (go - 0.5) * 1000, 0.8, 60);
    hiss(ctx, out, t0 + go, 800, "highpass", 4000, 0.7, 0.9);
    hiss(ctx, out, t0 + go, 500, "lowpass", 1200, 0.7, 0.6);
    thump(ctx, out, t0 + go, 120, 0.8);
    for (let i = 0; i < 9; i++) osc(ctx, out, t0 + go + 0.02 + i * 0.045, 2800 + Math.random() * 3200, 220, "triangle", 0.22);
  },

  // An AWP: the rifle comes up, a sharp supersonic crack and a deep boom
  // rolling off the walls (timed to the shot in the 3D animation, 0.48 s in),
  // then the bolt worked back and forth and the casing tinkling down.
  blaster: (ctx, out, t0) => {
    const shot = t0 + 0.48;
    // Rifle shouldered: a soft cloth rustle and a click.
    hiss(ctx, out, t0, 220, "bandpass", 1200, 0.8, 0.12, 0.08);
    hiss(ctx, out, t0 + 0.3, 18, "bandpass", 3500, 3, 0.35);
    // The crack: a hard, bright transient.
    hiss(ctx, out, shot, 45, "highpass", 2500, 0.6, 1, 0.001);
    osc(ctx, out, shot, 1900, 30, "square", 0.35, 0.001);
    // The body of the shot: a punchy boom with a quick falling pitch.
    hiss(ctx, out, shot, 420, "lowpass", 2200, 0.7, 1, 0.002).frequency.exponentialRampToValueAtTime(300, shot + 0.4);
    slide(ctx, out, shot, 160, 38, 450, "sine", 1, 0.002);
    slide(ctx, out, shot, 90, 30, 650, "triangle", 0.6, 0.004);
    // The tail rolling around the room.
    const e = echo(ctx, out, 0.11, 0.38, 3000);
    hiss(ctx, e, shot + 0.02, 600, "lowpass", 900, 0.7, 0.5, 0.01);
    hiss(ctx, out, shot + 0.05, 1300, "lowpass", 450, 0.7, 0.35, 0.05);
    // The bolt: up-back (clack), forward-down (clack-click).
    const bolt = t0 + 0.83;
    for (const [dt, f, lv] of [
      [0, 2600, 0.5],
      [0.12, 1800, 0.7],
      [0.3, 2200, 0.7],
      [0.38, 3200, 0.45],
    ] as const) {
      hiss(ctx, out, bolt + dt, 35, "bandpass", f, 3, lv);
      osc(ctx, out, bolt + dt, f * 0.6, 40, "square", 0.08 * lv);
    }
    // The brass casing bouncing on the table.
    for (const [dt, lv] of [
      [0.32, 1],
      [0.46, 0.6],
      [0.55, 0.35],
    ] as const) {
      osc(ctx, out, bolt + dt, 4200 + Math.random() * 400, 160, "sine", 0.18 * lv);
      osc(ctx, out, bolt + dt, 6100 + Math.random() * 400, 90, "sine", 0.1 * lv);
    }
  },

  // A blast, then stones clattering down.
  rocks: (ctx, out, t0) => {
    hiss(ctx, out, t0, 260, "lowpass", 1500, 0.7, 1);
    thump(ctx, out, t0, 100, 1);
    for (let i = 0; i < 16; i++) {
      const t = t0 + 0.38 + Math.random() * 0.6;
      hiss(ctx, out, t, 25, "bandpass", 1400 + Math.random() * 2200, 4, 0.6);
      osc(ctx, out, t, 500 + Math.random() * 700, 30, "square", 0.08);
    }
  },

  // A wet splat (after the lump has flown across).
  splat: (ctx, out, t0) => {
    const wh = hiss(ctx, out, t0, 700, "bandpass", 500, 1.5, 0.25, 0.3);
    wh.frequency.exponentialRampToValueAtTime(1100, t0 + 0.7);
    const f = hiss(ctx, out, t0 + 0.85, 320, "lowpass", 1100, 1.5, 1);
    f.frequency.exponentialRampToValueAtTime(180, t0 + 1.15);
    slide(ctx, out, t0 + 0.85, 230, 60, 200, "sine", 0.7);
    for (const dt of [1.05, 1.2, 1.38]) slide(ctx, out, t0 + dt, 800, 1500, 60, "sine", 0.22);
  },

  // A slimy squelch or three.
  squelch: (ctx, out, t0) => {
    for (const dt of [0.25, 0.5, 0.75]) {
      const f = hiss(ctx, out, t0 + dt, 200, "bandpass", 300, 6, 0.8, 0.03);
      f.frequency.exponentialRampToValueAtTime(1300, t0 + dt + 0.18);
      const o = slide(ctx, out, t0 + dt, 140, 260, 200, "sine", 0.35, 0.03);
      vibrato(ctx, o, t0 + dt, 200, 30, 25);
    }
    slide(ctx, out, t0 + 0.95, 380, 900, 90, "sine", 0.25);
  },

  // A monkey screaming: "ooh-ooh" hoots rising in pitch, then two harsh,
  // shrieking "aah-aah" screeches with a fast wobble.
  monkey: (ctx, out, t0) => {
    const voice = (t: number, f0: number, f1: number, ms: number, level: number, harsh: boolean) => {
      // A nasal voice: a buzzy sawtooth through a vowel filter, with vibrato.
      const vf = ctx.createBiquadFilter();
      vf.type = "bandpass";
      vf.frequency.setValueAtTime(harsh ? 1500 : 700, t);
      vf.frequency.exponentialRampToValueAtTime(harsh ? 2600 : 1100, t + ms / 1000);
      vf.Q.value = harsh ? 1.2 : 2.5;
      vf.connect(out);
      setTimeout(() => vf.disconnect(), (t - ctx.currentTime) * 1000 + ms + 400);
      const o = slide(ctx, vf, t, f0, f1, ms, "sawtooth", level, 0.02);
      vibrato(ctx, o, t, ms, harsh ? 26 : 14, harsh ? 70 : 30);
      const o2 = slide(ctx, vf, t, f0 * 1.51, f1 * 1.49, ms, "square", level * (harsh ? 0.5 : 0.2), 0.02);
      vibrato(ctx, o2, t, ms, harsh ? 31 : 12, harsh ? 90 : 40);
      if (harsh) hiss(ctx, out, t, ms, "bandpass", 3000, 1.5, level * 0.35, 0.03);
    };
    // Ooh-ooh-ooh, climbing.
    [0, 0.17, 0.32, 0.45].forEach((dt, i) => voice(t0 + dt, 380 + i * 90, 560 + i * 120, 140, 0.55, false));
    // Aah! Aah!
    voice(t0 + 0.62, 900, 1500, 320, 0.7, true);
    voice(t0 + 1.0, 1000, 1700, 420, 0.75, true);
  },

  // Control Weather: wind rising and a low rolling rumble.
  storm: (ctx, out, t0) => {
    const wind = hiss(ctx, out, t0, 1600, "bandpass", 400, 1.5, 0.5, 0.5);
    wind.frequency.exponentialRampToValueAtTime(900, t0 + 0.9);
    wind.frequency.exponentialRampToValueAtTime(350, t0 + 1.6);
    rumbleAt(ctx, out, t0 + 0.4, 1400, 0.7);
  },

  // Chain Lightning: a crackling electric zap that snaps twice.
  chainZap: (ctx, out, t0) => {
    for (const dt of [0, 0.16]) {
      const o = slide(ctx, out, t0 + dt, 1800, 180, 220, "sawtooth", 0.35, 0.003);
      vibrato(ctx, o, t0 + dt, 220, 60, 300);
      hiss(ctx, out, t0 + dt, 260, "highpass", 3000, 0.7, 0.6, 0.002);
    }
    thump(ctx, out, t0 + 0.18, 120, 0.5);
  },

  // Bonk trap set (only the caster hears it): a wooden knock and a sly tick.
  bonkCast: (ctx, out, t0) => {
    woodAt(ctx, out, t0, 0.5);
    woodAt(ctx, out, t0 + 0.14, 0.35);
    osc(ctx, out, t0 + 0.3, 1600, 60, "triangle", 0.3);
  },

  // Boom trap set (only the caster hears it): a fuse fizzing.
  boomCast: (ctx, out, t0) => {
    const f = hiss(ctx, out, t0, 700, "bandpass", 4000, 2, 0.5, 0.02);
    f.frequency.exponentialRampToValueAtTime(6500, t0 + 0.7);
    osc(ctx, out, t0 + 0.05, 900, 60, "square", 0.12);
  },

  // Treasure Finder / Fool's Treasure Finder (played on the announcement, identical for both): a chest lid creaks open, then a sparkly
  // coin-shower arpeggio.
  treasure: (ctx, out, t0) => {
    const creak = slide(ctx, out, t0, 180, 320, 260, "sawtooth", 0.18, 0.02);
    vibrato(ctx, creak, t0, 260, 35, 40);
    woodAt(ctx, out, t0 + 0.27, 0.4);
    const e = echo(ctx, out, 0.09, 0.3);
    [1047, 1319, 1568, 2093, 2637].forEach((fq, i) => {
      osc(ctx, out, t0 + 0.32 + i * 0.07, fq, 260, "triangle", 0.45);
      osc(ctx, e, t0 + 0.32 + i * 0.07, fq * 2, 120, "sine", 0.15);
    });
    for (let i = 0; i < 6; i++) osc(ctx, e, t0 + 0.5 + Math.random() * 0.5, 2500 + Math.random() * 2500, 70, "sine", 0.22);
  },

  // A lightning strike: a sharp crack, then thunder rolling away.
  thunder: (ctx, out, t0) => {
    hiss(ctx, out, t0, 140, "highpass", 2000, 0.6, 1, 0.001);
    hiss(ctx, out, t0 + 0.01, 400, "bandpass", 1200, 0.8, 0.8, 0.002);
    thump(ctx, out, t0 + 0.02, 110, 1);
    rumbleAt(ctx, out, t0 + 0.08, 1700, 1);
  },

  // Control Weather's cloud grumbling to itself (soft, every few seconds while it is up).
  rumble: (ctx, out, t0) => rumbleAt(ctx, out, t0, 2200, 0.8),

  // Bonk: a hollow wooden mallet hit with a springy cartoon boing.
  bonk: (ctx, out, t0) => {
    woodAt(ctx, out, t0, 1);
    thump(ctx, out, t0, 160, 0.9);
    const boing = slide(ctx, out, t0 + 0.05, 520, 260, 420, "sine", 0.5, 0.01);
    vibrato(ctx, boing, t0 + 0.05, 420, 16, 60);
    // Birdies tweeting round the head.
    for (const dt of [0.45, 0.62, 0.79]) slide(ctx, out, t0 + dt, 2400, 3200, 70, "sine", 0.18);
  },

  // The Boom trap's synth fallback (the recorded boomTrap.mp3 plays instead when it loads).
  boomTrap: (ctx, out, t0) => {
    hiss(ctx, out, t0, 1600, "lowpass", 1800, 0.7, 1, 0.002);
    thump(ctx, out, t0, 120, 1);
    slide(ctx, out, t0, 70, 28, 1200, "sine", 1, 0.005);
    rumbleAt(ctx, out, t0 + 0.1, 1500, 0.8);
  },
};
